import { useState, useRef, useEffect } from "react";
import { api, AgentPendingAction, AgentDiffField } from "../api";

type Msg = { role: "user" | "assistant"; text: string; sql?: string; action?: AgentPendingAction };

/** Trợ lý AI agent — hỏi đáp dữ liệu + đề xuất sửa SP (duyệt từng field). */
export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<Array<{ base64: string; mime_type: string; name: string; preview?: string }>>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [msgs, setMsgs] = useState<Msg[]>([
    { role: "assistant", text: "Chào bạn! Mình là trợ lý của shop. Mình tra được doanh thu, tồn kho, công nợ, chi phí, đơn hàng… và sửa/tạo sản phẩm. Cứ hỏi tự nhiên, hoặc gửi ảnh hóa đơn/hàng để mình đọc giúp nhé." },
  ]);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, open]);

  function pickFiles(files: FileList) {
    Array.from(files).forEach((file) => {
      if (file.size > 8 * 1024 * 1024) {
        alert(`File "${file.name}" quá lớn (>8MB), bỏ qua.`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result || "");
        const base64 = dataUrl.split(",")[1] || "";
        const mime_type = file.type || "application/octet-stream";
        const isImage = mime_type.startsWith("image/");
        setPendingFiles((prev) => [...prev, { base64, mime_type, name: file.name, preview: isImage ? dataUrl : undefined }]);
      };
      reader.readAsDataURL(file);
    });
  }

  async function send() {
    const q = input.trim();
    const files = pendingFiles;
    if ((!q && files.length === 0) || busy) return;
    setInput("");
    setPendingFiles([]);
    // Lịch sử hội thoại gần nhất (bỏ lời chào mở đầu) → để AI có ngữ cảnh "xóa nó đi" = SP vừa nhắc.
    const history = msgs
      .filter((m) => m.text && !m.text.startsWith("Chào bạn!"))
      .slice(-6)
      .map((m) => ({ role: m.role, content: m.text }));
    const fileLabel = files.length > 0 ? `📎 [${files.length} file] ` : "";
    setMsgs((m) => [...m, { role: "user", text: fileLabel + (q || "") }]);
    setBusy(true);
    try {
      const filesPayload = files.length > 0 ? files.map((f) => ({ base64: f.base64, mime_type: f.mime_type, name: f.name })) : undefined;
      // Tương thích ngược: nếu chỉ 1 ảnh, gửi cả image lẫn files
      const firstImage = files.find((f) => f.mime_type.startsWith("image/"));
      const imageCompat = firstImage ? { base64: firstImage.base64, mime_type: firstImage.mime_type } : undefined;
      const r = await api.ai.query(q || (files.length > 0 ? "Đọc file này giúp tôi" : ""), history, imageCompat, filesPayload);
      setMsgs((m) => [...m, { role: "assistant", text: r.answer || "Không có dữ liệu phù hợp.", sql: r.sql, action: r.pendingAction || undefined }]);
    } catch (e: any) {
      setMsgs((m) => [...m, { role: "assistant", text: "❌ " + (e?.message || "Lỗi truy vấn. Kiểm tra cấu hình AI ở Cấu hình AI.") }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        className="assistant-fab"
        onClick={() => setOpen((o) => !o)}
        title="Trợ lý hỏi đáp"
        style={{
          position: "fixed", right: 20, bottom: 88, zIndex: 60,
          width: 52, height: 52, borderRadius: "50%", border: "none", cursor: "pointer",
          background: "linear-gradient(135deg,#a78bfa,#7c5cff)", color: "#fff",
          boxShadow: "0 6px 20px rgba(124,92,255,0.4)", display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <span className="material-symbols-outlined">{open ? "close" : "auto_awesome"}</span>
      </button>

      {open && (
        <div
          style={{
            position: "fixed", right: 20, bottom: 152, zIndex: 60,
            width: "min(420px, calc(100vw - 24px))", height: "min(520px, calc(100vh - 200px))",
            background: "var(--surface, #1a1a24)", border: "1px solid var(--border, #2a2a38)",
            borderRadius: 14, boxShadow: "0 12px 40px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column", overflow: "hidden",
          }}
        >
          <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--border,#2a2a38)", display: "flex", alignItems: "center", gap: 8 }}>
            <span className="material-symbols-outlined" style={{ color: "#a78bfa" }}>auto_awesome</span>
            <strong style={{ fontSize: 14 }}>Trợ lý dữ liệu</strong>
          </div>

          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            {msgs.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: m.role === "user" ? "85%" : m.action ? "100%" : "85%", minWidth: 0, width: m.action ? "100%" : undefined, overflowX: m.action ? "hidden" : undefined }}>
                <div
                  style={{
                    padding: "8px 12px", borderRadius: 12, fontSize: 13, lineHeight: 1.45,
                    background: m.role === "user" ? "linear-gradient(135deg,#a78bfa,#7c5cff)" : "var(--surface-2,#24242f)",
                    color: m.role === "user" ? "#fff" : "var(--text,#e8e8ef)",
                  }}
                >
                  {m.role === "assistant" ? <MessageText text={m.text} /> : <span style={{ whiteSpace: "pre-wrap" }}>{m.text}</span>}
                </div>
                {m.sql && (
                  <details style={{ marginTop: 4, fontSize: 11, color: "var(--text-tertiary,#888)" }}>
                    <summary style={{ cursor: "pointer" }}>SQL</summary>
                    <code style={{ display: "block", marginTop: 4, padding: 6, background: "var(--surface-2,#24242f)", borderRadius: 6, overflowX: "auto" }}>{m.sql}</code>
                  </details>
                )}
                {m.action && <ActionCard action={m.action} />}
              </div>
            ))}
            {busy && <div style={{ fontSize: 13, color: "var(--text-tertiary,#888)" }}>Đang tra dữ liệu…</div>}
          </div>

          <div style={{ padding: 12, borderTop: "1px solid var(--border,#2a2a38)", display: "flex", flexDirection: "column", gap: 8 }}>
            {pendingFiles.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--text-tertiary,#aaa)" }}>
                {pendingFiles.map((f, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {f.preview
                      ? <img src={f.preview} alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 6, border: "1px solid var(--border,#2a2a38)" }} />
                      : <span style={{ width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, background: "var(--surface-2,#24242f)", border: "1px solid var(--border,#2a2a38)", fontSize: 18 }}>📄</span>}
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>{f.name}</span>
                    <button onClick={() => setPendingFiles((prev) => prev.filter((_, idx) => idx !== i))} style={{ border: "none", background: "transparent", color: "#f87171", cursor: "pointer", fontSize: 12 }}>Bỏ</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <input ref={fileRef} type="file" style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files; if (f && f.length) pickFiles(f); e.currentTarget.value = ""; }}
                accept="image/*,application/pdf" multiple />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                title="Gửi ảnh/PDF để AI đọc"
                style={{ width: 40, borderRadius: 9, border: "1px solid var(--border,#2a2a38)", cursor: busy ? "wait" : "pointer", background: "var(--surface-2,#24242f)", color: pendingFiles.length > 0 ? "#a78bfa" : "var(--text-secondary,#aaa)", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>attach_file</span>
              </button>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={pendingFiles.length > 0 ? "Thêm yêu cầu (tùy chọn)…" : "Hỏi, hoặc gửi ảnh/PDF hóa đơn…"}
                disabled={busy}
                style={{ flex: 1, padding: "9px 12px", borderRadius: 9, border: "1px solid var(--border,#2a2a38)", background: "var(--surface-2,#24242f)", color: "var(--text,#e8e8ef)", fontSize: 13 }}
              />
              <button
                onClick={send}
                disabled={busy || (!input.trim() && pendingFiles.length === 0)}
                style={{ width: 40, borderRadius: 9, border: "none", cursor: busy ? "wait" : "pointer", background: "linear-gradient(135deg,#a78bfa,#7c5cff)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>send</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function fmt(v: any): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return v.toLocaleString("vi-VN");
  return String(v);
}

/** Render text trợ lý: tách các khối bảng markdown (| a | b |) thành <table>, phần còn lại giữ text. */
function MessageText({ text }: { text: string }) {
  const lines = text.split("\n");
  const blocks: Array<{ type: "text"; lines: string[] } | { type: "table"; rows: string[][] }> = [];
  let i = 0;
  const isRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
  const isSep = (l: string) => /^\s*\|?\s*:?-{2,}.*\|/.test(l) || /^\s*\|(\s*:?-+:?\s*\|)+\s*$/.test(l);
  const cells = (l: string) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
  while (i < lines.length) {
    if (isRow(lines[i]) && i + 1 < lines.length && isSep(lines[i + 1])) {
      const rows: string[][] = [cells(lines[i])];
      i += 2; // bỏ header + separator
      while (i < lines.length && isRow(lines[i]) && !isSep(lines[i])) { rows.push(cells(lines[i])); i++; }
      blocks.push({ type: "table", rows });
    } else {
      const buf: string[] = [];
      while (i < lines.length && !(isRow(lines[i]) && i + 1 < lines.length && isSep(lines[i + 1]))) { buf.push(lines[i]); i++; }
      blocks.push({ type: "text", lines: buf });
    }
  }
  return (
    <>
      {blocks.map((b, bi) => {
        if (b.type === "text") {
          const t = b.lines.join("\n").trim();
          return t ? <div key={bi} style={{ whiteSpace: "pre-wrap" }}>{t}</div> : null;
        }
        const [head, ...body] = b.rows;
        // Heuristic: ô là số/tiền VN nếu chỉ gồm chữ số + dấu chấm/phẩy/khoảng trắng, kèm đ/% tùy chọn, có ít nhất 1 chữ số.
        const isNum = (s: string) => { const t = s.trim(); return /\d/.test(t) && /^-?[\d.,\s]+(?:đ|%|vnd|vnđ)?$/i.test(t); };
        // Cột căn phải nếu mọi ô data đều là số (cho phép ô rỗng/—) và có ít nhất 1 ô số thực.
        const colNum = head.map((_, ci) =>
          body.length > 0 &&
          body.every((r) => { const v = (r[ci] ?? "").trim(); return v === "" || v === "—" || isNum(v); }) &&
          body.some((r) => isNum((r[ci] ?? "").trim()))
        );
        const pad = head.length > 4 ? "3px 6px" : "4px 8px";
        return (
          <div key={bi} style={{ overflowX: "auto", margin: "6px 0" }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
              <thead>
                <tr>{head.map((h, hi) => <th key={hi} style={{ border: "1px solid var(--border,#3a3a48)", padding: pad, background: "var(--surface-2,#2c3340)", textAlign: colNum[hi] ? "right" : "left", whiteSpace: colNum[hi] ? "nowrap" : "normal", wordBreak: colNum[hi] ? "normal" : "break-word" }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {body.map((r, ri) => (
                  <tr key={ri}>{r.map((c, ci) => <td key={ci} style={{ border: "1px solid var(--border,#3a3a48)", padding: pad, textAlign: colNum[ci] ? "right" : "left", whiteSpace: colNum[ci] ? "nowrap" : "normal", wordBreak: colNum[ci] ? "normal" : "break-word" }}>{c}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </>
  );
}

/** Thẻ đề xuất hành động: update_product hiện bảng diff + checkbox từng field. */
function ActionCard({ action }: { action: AgentPendingAction }) {
  const [done, setDone] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [attachMode, setAttachMode] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [candidates, setCandidates] = useState<Array<{ id: number; supplier_name: string | null; total_amount: number; order_date: string | null; item_count: number }> | null>(null);
  const [sel, setSel] = useState<boolean[]>(
    action.type === "update_product" ? action.fields.map(() => true)
      : action.type === "bulk_action" ? action.rows.map((r) => r.selected !== false)
      : action.type === "create_product" ? action.rows.map((r) => r.selected !== false)
      : action.type === "create_import_order" ? action.rows.map((r) => r.selected !== false)
      : action.type === "mark_s1a" ? action.rows.map((r) => r.selected !== false)
      : []
  );

  async function apply() {
    if (applying) return;
    setApplying(true);
    try {
      let payload: any = action;
      if (action.type === "update_product") {
        const fields: AgentDiffField[] = action.fields
          .map((f, i) => ({ ...f, selected: sel[i] }))
          .filter((f) => f.selected);
        if (fields.length === 0) { setDone("⚠️ Chưa chọn field nào"); setApplying(false); return; }
        payload = { ...action, fields };
      } else if (action.type === "bulk_action") {
        const rows = action.rows.map((r, i) => ({ ...r, selected: sel[i] })).filter((r) => r.selected);
        if (rows.length === 0) { setDone("⚠️ Chưa chọn SP nào"); setApplying(false); return; }
        payload = { ...action, rows };
      } else if (action.type === "create_product") {
        const rows = action.rows.map((r, i) => ({ ...r, selected: sel[i] })).filter((r) => r.selected);
        if (rows.length === 0) { setDone("⚠️ Chưa chọn SP nào"); setApplying(false); return; }
        payload = { ...action, rows };
      } else if (action.type === "create_import_order") {
        const rows = action.rows.map((r, i) => ({ ...r, selected: sel[i] })).filter((r) => r.selected);
        if (rows.length === 0) { setDone("⚠️ Chưa chọn mặt hàng nào"); setApplying(false); return; }
        payload = { ...action, rows };
      } else if (action.type === "mark_s1a") {
        const rows = action.rows.map((r, i) => ({ ...r, selected: sel[i] })).filter((r) => r.selected);
        if (rows.length === 0) { setDone("⚠️ Chưa chọn SP nào"); setApplying(false); return; }
        payload = { ...action, rows };
      }
      const r = await api.ai.applyAction(payload);
      setDone((r.ok ? "✅ " : "❌ ") + r.message);
    } catch (e: any) {
      setDone("❌ " + (e?.message || "Lỗi apply"));
    } finally {
      setApplying(false);
    }
  }

  if (done) return <div style={{ marginTop: 6, fontSize: 12, color: "var(--text-secondary,#aaa)" }}>{done}</div>;

  const box: React.CSSProperties = { marginTop: 8, padding: 10, background: "var(--surface-2,#24242f)", border: "1px solid var(--border,#2a2a38)", borderRadius: 10, fontSize: 12 };
  const applyBtn = (
    <button onClick={apply} disabled={applying}
      style={{ marginTop: 8, width: "100%", padding: "7px 0", borderRadius: 8, border: "none", cursor: applying ? "wait" : "pointer", background: "linear-gradient(135deg,#34d399,#10b981)", color: "#04231a", fontWeight: 600 }}>
      {applying ? "Đang lưu…" : "✅ Áp dụng phần đã chọn"}
    </button>
  );

  if (action.type === "create_import_order") {
    const selectedCount = sel.filter(Boolean).length;
    const total = action.rows.reduce((s, r, i) => s + (sel[i] ? r.quantity * r.unit_cost : 0), 0);
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>
          📦 Đơn nhập{action.supplier_name ? ` · ${action.supplier_name}` : ""}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)", marginBottom: 6 }}>
          {selectedCount}/{action.rows.length} mặt hàng · tổng {fmt(total)}{action.currency === "VND" ? "đ" : " " + action.currency}
          {action.paid_amount > 0 ? ` · đã trả ${fmt(action.paid_amount)}` : " · nợ toàn bộ"}
        </div>
        <div style={{ maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
          {action.rows.map((r, i) => (
            <label key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer", padding: "3px 0", borderBottom: "1px solid var(--border-subtle,#222)" }}>
              <input type="checkbox" checked={sel[i]} onChange={() => setSel((s) => s.map((v, j) => (j === i ? !v : v)))} style={{ marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 600 }}>{r.item_name}</div>
                <div style={{ fontSize: 11, color: "var(--text-tertiary,#999)" }}>
                  {r.quantity} × {fmt(r.unit_cost)} = <span style={{ color: "#34d399" }}>{fmt(r.quantity * r.unit_cost)}</span>
                </div>
              </div>
            </label>
          ))}
        </div>
        <button onClick={apply} disabled={applying || attaching}
          style={{ marginTop: 8, width: "100%", padding: "7px 0", borderRadius: 8, border: "none", cursor: applying ? "wait" : "pointer", background: "linear-gradient(135deg,#34d399,#10b981)", color: "#04231a", fontWeight: 600 }}>
          {applying ? "Đang tạo…" : `📦 Tạo đơn nhập (${selectedCount})`}
        </button>
        <button
          onClick={async () => {
            setAttachMode(true); setCandidates(null);
            try {
              const r = await api.importOrders.match({ supplier_name: action.supplier_name, total_amount: total });
              setCandidates(r.candidates || []);
            } catch (e: any) { setCandidates([]); }
          }}
          disabled={applying || attaching}
          style={{ marginTop: 6, width: "100%", padding: "7px 0", borderRadius: 8, border: "1px solid var(--border,#3a3a48)", cursor: "pointer", background: "transparent", color: "var(--text,#ddd)", fontWeight: 600 }}>
          📎 Gắn vào đơn có sẵn
        </button>
        {attachMode && candidates === null && (
          <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-tertiary,#888)" }}>Đang tìm đơn…</div>
        )}
        {attachMode && candidates !== null && candidates.length === 0 && (
          <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-tertiary,#888)" }}>Không thấy đơn nhập khớp (30 ngày). Dùng nút Tạo đơn nếu là đơn mới.</div>
        )}
        {attachMode && candidates !== null && candidates.length > 0 && (
          <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>Chọn đơn để gắn chứng từ ({(action.attachments || []).length} file):</div>
            {candidates.map((o) => (
              <button key={o.id} disabled={attaching}
                onClick={async () => {
                  const files = action.attachments || [];
                  if (files.length === 0) { setDone("❌ Không có file đính kèm để gắn"); return; }
                  setAttaching(true);
                  try {
                    let n = 0;
                    for (const f of files) {
                      await api.importOrders.attachments.upload(o.id, { file_name: f.name, base64: f.base64, mime_type: f.mime_type });
                      n++;
                    }
                    setDone(`✅ Đã gắn ${n} file vào đơn #${o.id}`);
                  } catch (e: any) { setDone("❌ " + (e?.message || "Lỗi gắn file")); }
                  finally { setAttaching(false); }
                }}
                style={{ textAlign: "left", padding: "6px 8px", borderRadius: 8, border: "1px solid var(--border,#2a2a38)", cursor: attaching ? "wait" : "pointer", background: "var(--surface-3,#2c2c3a)", color: "var(--text,#ddd)", fontSize: 12 }}>
                #{o.id} · {o.supplier_name || "—"} · {fmt(o.total_amount)}đ{o.order_date ? ` · ${o.order_date}` : ""}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (action.type === "create_product") {
    const selectedCount = sel.filter(Boolean).length;
    const newCount = action.rows.filter((r) => !(r as any).existing_item_id).length;
    const oldCount = action.rows.length - newCount;
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>
          {[newCount > 0 ? `🆕 Tạo ${newCount} SP mới` : "", oldCount > 0 ? `🔄 Cộng tồn ${oldCount} SP cũ` : ""].filter(Boolean).join(" + ")}{action.category_name ? ` · danh mục "${action.category_name}"` : ""}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)", marginBottom: 6 }}>
          Đã chọn {selectedCount}/{action.rows.length} — bỏ chọn món không muốn rồi xác nhận.
        </div>
        <div style={{ maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
          {action.rows.map((r, i) => (
            <label key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer", padding: "3px 0", borderBottom: "1px solid var(--border-subtle,#222)" }}>
              <input type="checkbox" checked={sel[i]} onChange={() => setSel((s) => s.map((v, j) => (j === i ? !v : v)))} style={{ marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 600 }}>{r.item_name}</div>
                <div style={{ fontSize: 11, color: "var(--text-tertiary,#999)" }}>
                  {(r as any).existing_item_id ? (
                    <span style={{ color: "#fbbf24" }}>🔄 Cộng tồn +{r.initial_stock} vào SP POS: «{(r as any).existing_item_name || "?"}»</span>
                  ) : (r as any).existing_item_name ? (
                    <span style={{ color: "#f87171" }}>⚠️ Không thấy SP cũ «{(r as any).existing_item_name}» trên POS → sẽ TẠO MỚI</span>
                  ) : (
                    <>
                      Bán <span style={{ color: "#34d399" }}>{fmt(r.default_price)}</span>
                      {" · "}Nhập {fmt(r.cost)}
                      {" · "}SL {r.initial_stock}
                      {r.category_name && r.category_name !== action.category_name ? ` · ${r.category_name}` : ""}
                    </>
                  )}
                </div>
              </div>
            </label>
          ))}
        </div>
        <button onClick={apply} disabled={applying}
          style={{ marginTop: 8, width: "100%", padding: "7px 0", borderRadius: 8, border: "none", cursor: applying ? "wait" : "pointer", background: "linear-gradient(135deg,#34d399,#10b981)", color: "#04231a", fontWeight: 600 }}>
          {applying ? "Đang xử lý…" : `✅ Xác nhận ${selectedCount} món`}
        </button>
      </div>
    );
  }

  if (action.type === "update_product") {
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Sửa: {action.item_name}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {action.fields.map((f, i) => (
            <label key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
              <input type="checkbox" checked={sel[i]} onChange={() => setSel((s) => s.map((v, j) => (j === i ? !v : v)))} style={{ marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: "var(--text-tertiary,#888)" }}>
                  {f.label}{f.scope === "variant" ? ` · ${f.variant_label || ""}` : ""}
                  {f.key === "initial_stock" ? " ⚠️ Loyverse" : ""}
                </div>
                <div><span style={{ color: "var(--accent-red,#f87171)", textDecoration: "line-through" }}>{fmt(f.old)}</span>{" → "}<span style={{ color: "var(--accent-green,#34d399)", fontWeight: 600 }}>{fmt(f.new)}</span></div>
              </div>
            </label>
          ))}
        </div>
        {applyBtn}
      </div>
    );
  }

  if (action.type === "bulk_action") {
    const isDelete = action.op === "delete";
    const selectedCount = sel.filter(Boolean).length;
    const allSelected = action.rows.length > 0 && selectedCount === action.rows.length;
    return (
      <div style={{ ...box, border: isDelete ? "1px solid #f87171" : "1px solid var(--border,#2a2a38)", overflowX: "hidden", minWidth: 0, maxWidth: "100%" }}>
        <div style={{ fontWeight: 600, marginBottom: 2, color: isDelete ? "#f87171" : undefined, wordBreak: "break-word" }}>
          {isDelete ? "🗑 " : "✏️ "}{action.label}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <span>Đã chọn {selectedCount}/{action.rows.length} SP — bỏ chọn dòng không muốn rồi áp dụng.</span>
          <button
            type="button"
            onClick={() => setSel(action.rows.map(() => !allSelected))}
            style={{ border: "none", background: "none", color: "#a78bfa", cursor: "pointer", fontSize: 11, padding: 0, whiteSpace: "nowrap", flexShrink: 0 }}
          >
            {allSelected ? "Bỏ chọn" : "Chọn hết"}
          </button>
        </div>
        <div style={{ maxHeight: 280, overflowY: "auto", overflowX: "hidden", display: "flex", flexDirection: "column", gap: 4 }}>
          {action.rows.map((r, i) => {
            const rawName = String(r.item_name ?? "").trim();
            const rawSku = String(r.sku ?? "").trim();
            const shortId = r.item_id ? `#${String(r.item_id).slice(0, 8)}` : "";
            const name = rawName || rawSku || shortId || "SP (không tên)";
            const showSku = !!(rawSku && rawName && rawSku !== rawName);
            return (
              <label key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer", minHeight: 36, padding: "6px 0", borderBottom: "1px solid var(--border-subtle,#222)", overflow: "hidden", minWidth: 0, width: "100%", boxSizing: "border-box" }}>
                <input type="checkbox" checked={sel[i]} onChange={() => setSel((s) => s.map((v, j) => (j === i ? !v : v)))} style={{ flexShrink: 0, marginTop: 3 }} />
                <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                  <div style={{ fontWeight: 600, lineHeight: 1.35, color: "#e8e8f0", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as const, overflow: "hidden", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {name}
                    {showSku ? <span style={{ fontWeight: 400, color: "#888", marginLeft: 6 }}>{rawSku}</span> : null}
                  </div>
                  {!isDelete && (
                    <div style={{ fontSize: 11, marginTop: 2, lineHeight: 1.35, color: "#c4c4d0", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                      <span style={{ color: "#f87171", textDecoration: "line-through" }}>{fmt(r.old)}</span>
                      {" → "}
                      <span style={{ color: "#34d399", fontWeight: 600 }}>{fmt(r.new)}</span>
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>
        <button onClick={apply} disabled={applying}
          style={{ marginTop: 8, width: "100%", padding: "7px 0", borderRadius: 8, border: "none", cursor: applying ? "wait" : "pointer", background: isDelete ? "linear-gradient(135deg,#f87171,#dc2626)" : "linear-gradient(135deg,#34d399,#10b981)", color: isDelete ? "#fff" : "#04231a", fontWeight: 600 }}>
          {applying ? "Đang áp dụng…" : isDelete ? `🗑 Xoá ${selectedCount} SP` : `✅ Áp dụng ${selectedCount} SP`}
        </button>
      </div>
    );
  }

  if (action.type === "mark_s1a") {
    const selectedCount = sel.filter(Boolean).length;
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>🧾 {action.label}</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)", marginBottom: 6 }}>
          Đã chọn {selectedCount}/{action.rows.length} SP — đánh dấu vào kho ảo + sổ S1A.
        </div>
        <div style={{ maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
          {action.rows.map((r, i) => (
            <label key={i} style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer", padding: "3px 0", borderBottom: "1px solid var(--border-subtle,#222)" }}>
              <input type="checkbox" checked={sel[i]} onChange={() => setSel((s) => s.map((v, j) => (j === i ? !v : v)))} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.item_name}</div>
                <div style={{ fontSize: 11 }}>
                  <span style={{ color: "#f87171" }}>{String(r.old)}</span>
                  {" → "}<span style={{ color: "#34d399", fontWeight: 600 }}>{String(r.new)}</span>
                </div>
              </div>
            </label>
          ))}
        </div>
        {applyBtn}
      </div>
    );
  }

  if (action.type === "delete_product") {
    return (
      <div style={{ ...box, border: "1px solid #f87171" }}>
        <div style={{ fontWeight: 600, marginBottom: 4, color: "#f87171" }}>🗑 Xoá hẳn sản phẩm</div>
        <div style={{ marginBottom: 2 }}>{action.item_name}</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>⚠️ Xoá khỏi hệ thống và Loyverse, không thể hoàn tác.</div>
        <button onClick={apply} disabled={applying}
          style={{ marginTop: 8, width: "100%", padding: "7px 0", borderRadius: 8, border: "none", cursor: applying ? "wait" : "pointer", background: "linear-gradient(135deg,#f87171,#dc2626)", color: "#fff", fontWeight: 600 }}>
          {applying ? "Đang xoá…" : "🗑 Xác nhận xoá hẳn"}
        </button>
      </div>
    );
  }

  if (action.type === "add_expense") {
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Ghi chi phí</div>
        <div>{fmt(action.amount)}đ · {action.category}{action.description ? ` · ${action.description}` : ""}</div>
        {applyBtn}
      </div>
    );
  }

  if (action.type === "add_asset") {
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>🏷️ Ghi tài sản</div>
        <div>{action.name} · {fmt(action.purchase_price)}đ</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>
          {action.category} · khấu hao {action.depreciation_years} năm{action.purchase_date ? ` · mua ${action.purchase_date}` : ""}
        </div>
        {applyBtn}
      </div>
    );
  }

  if (action.type === "create_todo") {
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Tạo việc cần làm</div>
        <div>{action.title}{action.due_date ? ` · hạn ${action.due_date}` : ""}</div>
        {applyBtn}
      </div>
    );
  }

  // ─── Các action mới: thẻ xác nhận đơn giản (1 nút Áp dụng) ───
  if (action.type === "pay_payable") {
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>💵 Trả công nợ · {action.supplier_name}</div>
        <div>{fmt(action.amount)}đ ({action.payment_method}) · còn nợ sau: <span style={{ color: "#34d399" }}>{fmt(action.remaining_after)}đ</span></div>
        {applyBtn}
      </div>
    );
  }
  if (action.type === "receive_import") {
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>📦 Nhập kho đơn #{action.import_order_id}</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)", marginBottom: 6 }}>Cộng tồn {action.rows.length} SP + đẩy Loyverse{action.unmapped?.length ? ` · ${action.unmapped.length} dòng chưa map sẽ bỏ qua` : ""}</div>
        <div style={{ maxHeight: 200, overflowY: "auto" }}>
          {action.rows.map((r, i) => <div key={i} style={{ fontSize: 12, padding: "2px 0" }}>• {r.item_name}: +{r.quantity}</div>)}
        </div>
        {applyBtn}
      </div>
    );
  }
  if (action.type === "adjust_stock") {
    const d = action.new_stock - action.old_stock;
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>📊 Chỉnh tồn · {action.item_name}</div>
        <div>{action.old_stock} → <span style={{ color: "#34d399", fontWeight: 600 }}>{action.new_stock}</span> ({d >= 0 ? "+" : ""}{d})</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>Lý do: {action.reason}</div>
        {applyBtn}
      </div>
    );
  }
  if (action.type === "map_import_items") {
    const newCount = action.new_product_item_ids?.length ?? 0;
    const parts: string[] = [];
    if (action.rows.length) parts.push(`Map ${action.rows.length} dòng`);
    if (newCount) parts.push(`${newCount} hàng mới`);
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>🔗 {parts.join(" · ")} · đơn #{action.import_order_id}</div>
        <div style={{ maxHeight: 200, overflowY: "auto" }}>
          {action.rows.map((r, i) => <div key={i} style={{ fontSize: 12, padding: "2px 0" }}>• {r.line_item_name} → <b>{r.mapped_item_name}</b></div>)}
        </div>
        {newCount > 0 && (
          <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)", marginTop: 4 }}>+ {newCount} dòng đánh dấu hàng mới (sẽ tạo SP mới khi nhập kho)</div>
        )}
        {applyBtn}
      </div>
    );
  }
  if (action.type === "batch_match_import_items") {
    const mapped = action.results.filter((r) => !r.is_new && r.best_match);
    const newItems = action.results.filter((r) => r.is_new);
    const border = action.results.filter((r) => r.confidence !== "high" && !r.is_new);
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>🔍 Phân loại {action.results.length} dòng · đơn #{action.import_order_id}{action.all_confident ? " (tất cả rõ)" : " (có dòng borderline)"}</div>
        <div style={{ maxHeight: 220, overflowY: "auto" }}>
          {mapped.map((r, i) => (
            <div key={i} style={{ fontSize: 12, padding: "2px 0", color: r.confidence !== "high" ? "var(--text-tertiary,#888)" : undefined }}>
              • #{r.line_id} {r.line_name} → <b>{r.best_match!.item_name}</b>{r.confidence !== "high" ? " ⚠ borderline" : ""}
            </div>
          ))}
          {newItems.map((r, i) => (
            <div key={"n" + i} style={{ fontSize: 12, padding: "2px 0", color: "var(--text-tertiary,#888)" }}>• #{r.line_id} {r.line_name} → <i>HÀNG MỚI</i></div>
          ))}
        </div>
        {border.length > 0 && (
          <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 4 }}>{border.length} dòng borderline — user chọn SP trên web/AI rồi map.</div>
        )}
        <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)", marginTop: 4 }}>
          {action.all_confident ? "User duyệt → AI gọi propose_map_import_items" : "Chưa duyệt được — còn dòng borderline."}
        </div>
      </div>
    );
  }
  if (action.type === "upload_product_image") {
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>📸 Gắn ảnh · {action.item_name}</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>Cắt vuông + đẩy lên Loyverse.</div>
        {applyBtn}
      </div>
    );
  }
  if (action.type === "add_supplier") {
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>🏪 {action.existing_id ? "Cập nhật" : "Thêm"} NCC · {action.name}</div>
        {action.phone && <div style={{ fontSize: 12 }}>📞 {action.phone}</div>}
        {action.address && <div style={{ fontSize: 12 }}>📍 {action.address}</div>}
        {applyBtn}
      </div>
    );
  }
  if (action.type === "add_category") {
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>🏷️ Tạo danh mục · {action.name}</div>
        {applyBtn}
      </div>
    );
  }
  if (action.type === "delete_import_order") {
    return (
      <div style={{ ...box, border: "1px solid #f87171" }}>
        <div style={{ fontWeight: 600, marginBottom: 4, color: "#f87171" }}>🗑 Xóa đơn nhập #{action.import_order_id}</div>
        <div style={{ fontSize: 12 }}>{action.supplier_name || ""} · {action.item_count} dòng · {fmt(action.total_amount)}đ</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>⚠️ Gỡ cả công nợ + VAT đã gắn. Không hoàn tác.</div>
        <button onClick={apply} disabled={applying}
          style={{ marginTop: 8, width: "100%", padding: "7px 0", borderRadius: 8, border: "none", cursor: applying ? "wait" : "pointer", background: "linear-gradient(135deg,#f87171,#dc2626)", color: "#fff", fontWeight: 600 }}>
          {applying ? "Đang xóa…" : "🗑 Xác nhận xóa"}
        </button>
      </div>
    );
  }
  if (action.type === "add_purchase_ledger") {
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>🧾 Sổ mua hàng</div>
        <div>{action.description} · <span style={{ color: "#34d399" }}>{fmt(action.amount)}đ</span>{action.category ? ` · ${action.category}` : ""}</div>
        {applyBtn}
      </div>
    );
  }
  if (action.type === "update_supplier") {
    const name = action.name || `NCC #${action.supplier_id}`;
    if (action.delete) {
      return (
        <div style={{ ...box, border: "1px solid #f87171" }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: "#f87171" }}>🗑 XÓA NCC · {name}</div>
          <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>⚠️ Xóa nhà cung cấp khỏi hệ thống. Không hoàn tác.</div>
          <button onClick={apply} disabled={applying}
            style={{ marginTop: 8, width: "100%", padding: "7px 0", borderRadius: 8, border: "none", cursor: applying ? "wait" : "pointer", background: "linear-gradient(135deg,#f87171,#dc2626)", color: "#fff", fontWeight: 600 }}>
            {applying ? "Đang xóa…" : "🗑 Xác nhận xóa"}
          </button>
        </div>
      );
    }
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>🏪 Cập nhật NCC · {name}</div>
        {action.phone && <div style={{ fontSize: 12 }}>📞 {action.phone}</div>}
        {action.address && <div style={{ fontSize: 12 }}>📍 {action.address}</div>}
        {action.tax_id && <div style={{ fontSize: 12 }}>🧾 MST: {action.tax_id}</div>}
        {action.bank_account && <div style={{ fontSize: 12 }}>🏦 {action.bank_account}</div>}
        {applyBtn}
      </div>
    );
  }
  if (action.type === "rate_supplier") {
    const name = action.supplier_name || `NCC #${action.supplier_id}`;
    const parts: string[] = [];
    if (action.delivery != null) parts.push(`Giao hàng ${action.delivery}`);
    if (action.quality != null) parts.push(`Chất lượng ${action.quality}`);
    if (action.price != null) parts.push(`Giá ${action.price}`);
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>⭐ Đánh giá NCC · {name}</div>
        {parts.length > 0 && <div style={{ fontSize: 12 }}>{parts.join(" · ")}</div>}
        {action.note && <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>Ghi chú: {action.note}</div>}
        {applyBtn}
      </div>
    );
  }
  if (action.type === "manage_expense") {
    if (action.delete) {
      return (
        <div style={{ ...box, border: "1px solid #f87171" }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: "#f87171" }}>🗑 XÓA chi phí #{action.expense_id}</div>
          {action.amount != null && <div style={{ fontSize: 12 }}>{fmt(action.amount)}đ{action.category ? ` · ${action.category}` : ""}</div>}
          <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>⚠️ Xóa khoản chi phí khỏi sổ. Không hoàn tác.</div>
          <button onClick={apply} disabled={applying}
            style={{ marginTop: 8, width: "100%", padding: "7px 0", borderRadius: 8, border: "none", cursor: applying ? "wait" : "pointer", background: "linear-gradient(135deg,#f87171,#dc2626)", color: "#fff", fontWeight: 600 }}>
            {applying ? "Đang xóa…" : "🗑 Xác nhận xóa"}
          </button>
        </div>
      );
    }
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>💸 Cập nhật chi phí #{action.expense_id}</div>
        <div style={{ fontSize: 12 }}>
          {action.amount != null ? <span style={{ color: "#34d399" }}>{fmt(action.amount)}đ</span> : null}
          {action.category ? `${action.amount != null ? " · " : ""}${action.category}` : ""}
          {action.date ? ` · ${action.date}` : ""}
        </div>
        {action.note && <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>Ghi chú: {action.note}</div>}
        {applyBtn}
      </div>
    );
  }
  if (action.type === "manage_asset") {
    const name = action.name || `Tài sản #${action.asset_id}`;
    if (action.delete) {
      return (
        <div style={{ ...box, border: "1px solid #f87171" }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: "#f87171" }}>🗑 XÓA tài sản · {name}</div>
          <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>⚠️ Xóa tài sản khỏi hệ thống. Không hoàn tác.</div>
          <button onClick={apply} disabled={applying}
            style={{ marginTop: 8, width: "100%", padding: "7px 0", borderRadius: 8, border: "none", cursor: applying ? "wait" : "pointer", background: "linear-gradient(135deg,#f87171,#dc2626)", color: "#fff", fontWeight: 600 }}>
            {applying ? "Đang xóa…" : "🗑 Xác nhận xóa"}
          </button>
        </div>
      );
    }
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>🏷️ Cập nhật tài sản · {name}</div>
        {action.value != null && <div style={{ fontSize: 12 }}><span style={{ color: "#34d399" }}>{fmt(action.value)}đ</span></div>}
        {action.note && <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>Ghi chú: {action.note}</div>}
        {applyBtn}
      </div>
    );
  }
  if (action.type === "manage_todo") {
    const title = action.title || `Việc #${action.todo_id}`;
    if (action.delete) {
      return (
        <div style={{ ...box, border: "1px solid #f87171" }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: "#f87171" }}>🗑 XÓA việc · {title}</div>
          <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>⚠️ Xóa việc khỏi danh sách. Không hoàn tác.</div>
          <button onClick={apply} disabled={applying}
            style={{ marginTop: 8, width: "100%", padding: "7px 0", borderRadius: 8, border: "none", cursor: applying ? "wait" : "pointer", background: "linear-gradient(135deg,#f87171,#dc2626)", color: "#fff", fontWeight: 600 }}>
            {applying ? "Đang xóa…" : "🗑 Xác nhận xóa"}
          </button>
        </div>
      );
    }
    const isDone = action.status === "done";
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{isDone ? "✅ Hoàn thành việc" : "📝 Cập nhật việc"} · {title}</div>
        {action.status && !isDone && <div style={{ fontSize: 12 }}>Trạng thái: {action.status}</div>}
        {applyBtn}
      </div>
    );
  }

  if (action.type === "manage_purchase_ledger") {
    if (action.delete) {
      return (
        <div style={{ ...box, border: "1px solid #f87171" }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: "#f87171" }}>🗑 XÓA dòng bảng kê #{action.ledger_id}</div>
          {action.seller_name && <div style={{ fontSize: 12 }}>{action.seller_name}</div>}
          {action.total_amount != null && <div style={{ fontSize: 12 }}><span style={{ color: "#34d399" }}>{fmt(action.total_amount)}đ</span></div>}
          {action.warn_synced && <div style={{ fontSize: 11, color: "#fbbf24" }}>⚠️ Dòng gắn đơn nhập, sửa tay có thể bị ghi đè khi sync lại</div>}
          <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>⚠️ XÓA dòng bảng kê · Không hoàn tác</div>
          <button onClick={apply} disabled={applying}
            style={{ marginTop: 8, width: "100%", padding: "7px 0", borderRadius: 8, border: "none", cursor: applying ? "wait" : "pointer", background: "linear-gradient(135deg,#f87171,#dc2626)", color: "#fff", fontWeight: 600 }}>
            {applying ? "Đang xóa…" : "🗑 Xác nhận xóa"}
          </button>
        </div>
      );
    }
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>🧾 Sửa bảng kê #{action.ledger_id}</div>
        {action.seller_name && <div style={{ fontSize: 12 }}>{action.seller_name}</div>}
        {action.seller_address && <div style={{ fontSize: 12 }}>📍 {action.seller_address}</div>}
        {action.seller_id_card && <div style={{ fontSize: 12 }}>🪪 {action.seller_id_card}</div>}
        {action.item_description && <div style={{ fontSize: 12 }}>{action.item_description}</div>}
        {(action.quantity != null || action.unit_price != null) && (
          <div style={{ fontSize: 12 }}>
            {action.quantity != null ? `${action.quantity}` : ""}{action.unit_price != null ? ` × ${fmt(action.unit_price)}` : ""}
          </div>
        )}
        {action.total_amount != null && <div style={{ fontSize: 12 }}><span style={{ color: "#34d399" }}>{fmt(action.total_amount)}đ</span></div>}
        {action.warn_synced && <div style={{ fontSize: 11, color: "#fbbf24" }}>⚠️ Dòng gắn đơn nhập, sửa tay có thể bị ghi đè khi sync lại</div>}
        {applyBtn}
      </div>
    );
  }
  if (action.type === "manage_vat_invoice") {
    if (action.delete) {
      return (
        <div style={{ ...box, border: "1px solid #f87171" }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: "#f87171" }}>🗑 XÓA hóa đơn VAT #{action.invoice_id}</div>
          {action.invoice_number && <div style={{ fontSize: 12 }}>Số HĐ: {action.invoice_number}</div>}
          {action.seller_name && <div style={{ fontSize: 12 }}>{action.seller_name}</div>}
          {action.vat_amount != null && <div style={{ fontSize: 12 }}>VAT: <span style={{ color: "#34d399" }}>{fmt(action.vat_amount)}đ</span></div>}
          {action.warn_synced && <div style={{ fontSize: 11, color: "#fbbf24" }}>⚠️ Hóa đơn gắn đơn nhập, sửa tay có thể bị ghi đè khi sync lại</div>}
          <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>⚠️ XÓA hóa đơn VAT · Không hoàn tác</div>
          <button onClick={apply} disabled={applying}
            style={{ marginTop: 8, width: "100%", padding: "7px 0", borderRadius: 8, border: "none", cursor: applying ? "wait" : "pointer", background: "linear-gradient(135deg,#f87171,#dc2626)", color: "#fff", fontWeight: 600 }}>
            {applying ? "Đang xóa…" : "🗑 Xác nhận xóa"}
          </button>
        </div>
      );
    }
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>🧾 Sửa hóa đơn VAT #{action.invoice_id}</div>
        {action.invoice_number && <div style={{ fontSize: 12 }}>Số HĐ: {action.invoice_number}</div>}
        {action.invoice_date && <div style={{ fontSize: 12 }}>📅 {action.invoice_date}</div>}
        {action.seller_name && <div style={{ fontSize: 12 }}>{action.seller_name}</div>}
        {action.seller_tax_id && <div style={{ fontSize: 12 }}>🧾 MST: {action.seller_tax_id}</div>}
        {action.amount_before_vat != null && <div style={{ fontSize: 12 }}>Trước VAT: {fmt(action.amount_before_vat)}đ</div>}
        {action.vat_amount != null && <div style={{ fontSize: 12 }}>VAT: <span style={{ color: "#34d399" }}>{fmt(action.vat_amount)}đ</span></div>}
        {action.warn_synced && <div style={{ fontSize: 11, color: "#fbbf24" }}>⚠️ Hóa đơn gắn đơn nhập, sửa tay có thể bị ghi đè khi sync lại</div>}
        {applyBtn}
      </div>
    );
  }
  if (action.type === "assign_consignment") {
    const name = action.supplier_name || `NCC #${action.supplier_id}`;
    const count = action.count != null ? action.count : (action.item_ids ? action.item_ids.length : (action.item_id != null ? 1 : 0));
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          🤝 Gán ký gửi · {count} SP/{name}{action.commission_rate != null ? ` · HH ${action.commission_rate}%` : ""}
        </div>
        {action.note && <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>Ghi chú: {action.note}</div>}
        {applyBtn}
      </div>
    );
  }
  if (action.type === "settle_consignment") {
    const name = action.supplier_name || `NCC #${action.supplier_id}`;
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>📊 Đối soát ký gửi · {name}</div>
        {(action.period_from || action.period_to) && (
          <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)" }}>
            Kỳ: {action.period_from || "…"} → {action.period_to || "…"}
          </div>
        )}
        {action.revenue != null && <div style={{ fontSize: 12 }}>Doanh thu: <span style={{ color: "#34d399" }}>{fmt(action.revenue)}đ</span></div>}
        {action.commission_rate != null && <div style={{ fontSize: 12 }}>Hoa hồng: {action.commission_rate}%</div>}
        {action.payable_amount != null && <div style={{ fontSize: 13, fontWeight: 600 }}>Phải trả NCC: <span style={{ color: "#fbbf24" }}>{fmt(action.payable_amount)}đ</span></div>}
        <div style={{ fontSize: 11, color: "var(--text-tertiary,#888)", marginTop: 2 }}>Sẽ tạo công nợ phải trả NCC</div>
        {applyBtn}
      </div>
    );
  }
  if (action.type === "stock_take") {
    return (
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>📋 Kiểm kê · {action.rows.length} SP</div>
        <div style={{ maxHeight: 200, overflowY: "auto" }}>
          {action.rows.map((r, i) => {
            const c = r.delta > 0 ? "#34d399" : r.delta < 0 ? "#f87171" : "#888";
            return (
              <div key={i} style={{ fontSize: 12, padding: "2px 0" }}>
                • {r.item_name}: {r.old_stock} → {r.actual_qty} <span style={{ color: c }}>({r.delta >= 0 ? "+" : ""}{r.delta})</span>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 4 }}>⚠️ Ghi đè tồn kho thực tế + đẩy Loyverse.</div>
        {applyBtn}
      </div>
    );
  }

  return null;
}
